import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as Y from 'yjs';
import { BlockNoteEditor } from '@blocknote/core';
import { useCreateBlockNote } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/mantine';
import { yDocToBlocks } from '@blocknote/core/yjs';
import { supabase } from '../supabaseClient';
import { base64ToU8 } from '../features/editor/collab/yjsBase64';
import { normalizeFragmentRoots } from '../features/editor/useCollab';
import { schema } from '../features/editor/blocks/schema';
import { createInternalLinkClickHandler } from '../features/editor/internalLinkNav';
import useTheme from '../lib/useTheme';
import '@blocknote/mantine/style.css';
import './public-page.css';

/**
 * 공개 읽기전용 페이지 — 로그인 불필요.
 * 라우트: /public/:token
 * DB: get_public_page(p_token) -> { ok, title, icon, cover, ystate, allow_comment }
 *     실패 시 { ok:false, error:'not_found'|'expired' }
 * useAuth 사용 금지 (anon 접근).
 */
export default function PublicPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // 'not_found' | 'expired' | 'unknown'
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setData(null);
    if (!token) { setError('not_found'); setLoading(false); return () => { active = false; }; }
    (async () => {
      const { data: res, error: rpcErr } = await supabase.rpc('get_public_page', { p_token: token });
      if (!active) return;
      if (rpcErr) {
        setError('unknown');
      } else if (!res || res.ok === false) {
        setError((res && res.error) || 'not_found');
      } else {
        setData(res);
      }
      setLoading(false);
    })();
    return () => { active = false; };
  }, [token]);

  if (loading) {
    return (
      <div className="pp-state">
        <div className="spinner" />
        <div className="muted" style={{ fontSize: 13 }}>페이지를 불러오는 중…</div>
      </div>
    );
  }

  if (error) {
    const isExpired = error === 'expired';
    return (
      <div className="pp-state">
        <div className="pp-state-icon">{isExpired ? '⏳' : '🔒'}</div>
        <div className="pp-state-title">
          {isExpired ? '만료된 링크입니다' : '페이지를 찾을 수 없습니다'}
        </div>
        <div className="pp-state-sub">
          {isExpired
            ? '이 공개 링크는 더 이상 사용할 수 없습니다.'
            : '링크가 잘못되었거나 공개가 해제되었습니다.'}
        </div>
        <div className="pp-foot">루나르트로 만든 페이지</div>
      </div>
    );
  }

  return <PublicContent data={data} />;
}

/**
 * ystate 로드가 끝난 데이터를 받아 읽기전용 에디터를 마운트한다.
 * data 가 준비된 뒤에만 렌더되므로 doc 초기화가 안전하다.
 */
function PublicContent({ data }) {
  const { title, icon, cover, ystate, settings } = data;
  const fullWidth = !!(settings && settings.fullWidth);
  const navigate = useNavigate();

  // 이슈 A — 공개(읽기전용) 페이지도 본문 안 내부 링크(href="/page/<uuid>") 클릭 시 새 탭 대신
  // 같은 앱 안에서 전환한다. PageEditor.jsx 와 동일한 원리(캡처 단계 클릭 리스너, 내부 링크만
  // preventDefault+navigate, 외부 링크/수정자 클릭은 기존 새 탭 동작 유지) — internalLinkNav.js 참조.
  const editorContainerRef = useRef(null);
  useEffect(() => {
    const container = editorContainerRef.current;
    if (!container) return undefined;
    const handleClick = createInternalLinkClickHandler(navigate, () => editorContainerRef.current);
    container.addEventListener('click', handleClick, true);
    return () => container.removeEventListener('click', handleClick, true);
  }, [navigate]);

  return (
    <div className="pp-root">
      {cover ? <div className="pp-cover" style={{ background: cover }} /> : null}

      <div className={`pp-doc${cover ? ' has-cover' : ''}${fullWidth ? ' pp-full-width' : ''}`}>
        <div className="pp-icon">{icon || '📄'}</div>
        <h1 className="pp-title">{title || '제목 없음'}</h1>

        <div className="pp-editor" ref={editorContainerRef}>
          {/* ystate 값에 따라 안전하게 재초기화하기 위해 key 부여 */}
          <ReadonlyDoc key={ystate ? `y-${ystate.length}` : 'empty'} ystate={ystate} />
        </div>

        <div className="pp-foot">루나르트로 만든 페이지</div>
      </div>
    </div>
  );
}

function ReadonlyDoc({ ystate }) {
  // 실효 테마 — 공개(비로그인) 라우트에서도 ThemeProvider(index.js 최상단)가 감싸므로 동작
  const { theme } = useTheme();
  // ystate(base64 Yjs 업데이트) → BlockNote 블록으로 1회 디코드.
  //
  // ⚠️ 과거엔 collaboration 모드로 fragment 에 바인딩해 렌더했는데, y-prosemirror
  //    초기 동기화가 "빠른" 로드(캐시된 새로고침 등)에서 누락돼 본문이 백지로 남는
  //    레이스가 있었다(공개 페이지 본문 빈 화면 버그). 데이터는 정상인데 안 그려짐.
  //    → 읽기 전용 뷰는 실시간이 필요 없으므로 collaboration 을 쓰지 않고,
  //      yDocToBlocks 로 스냅샷을 블록으로 변환해 initialContent 로 정적 렌더한다.
  //      마운트 타이밍과 무관하게 항상 같은 결과(결정적). markdownToYstate 가
  //      blocksToYDoc(editor, blocks, fragment) 로 직렬화하는 것의 정확한 역연산.
  const blocks = useMemo(() => {
    if (!ystate) return null;
    try {
      const doc = new Y.Doc();
      Y.applyUpdate(doc, base64ToU8(ystate));
      // 다중 blockGroup 루트 오염 치유(읽기 전용 — 서버 저장 없음): 병합 순서상 빈 그룹이
      // 앞이면 첫 그룹만 채택되어 본문이 공백으로 렌더된다 → 변환 전에 정규화(2026-07-05).
      normalizeFragmentRoots(doc);
      // 변환 전용 헤드리스 에디터(마운트 X) — 라이브 에디터와 동일 schema.
      const headless = BlockNoteEditor.create({ schema });
      const out = yDocToBlocks(headless, doc, 'document-store');
      doc.destroy();
      return Array.isArray(out) && out.length ? out : null;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('[PUBLIC DEBUG] error', e && e.message);
      return null;
    }
  }, [ystate]);

  // 라이브 에디터(PageEditor)·헤드리스 변환기(markdownToYstate)와 동일한 커스텀 스키마라야
  // 토글 헤딩·콜아웃·linkedDatabaseView·수식·북마크·동기화 블록·임베드가 동일하게 렌더된다
  // (기본 schema 면 커스텀 블록 드롭).
  const editor = useCreateBlockNote({ schema, initialContent: blocks || undefined }, [blocks]);

  // 커스텀 블록(수식/북마크/임베드/동기화 블록)은 render 시 editor.isEditable 로 편집 UI 여부를
  // 결정한다. BlockNoteView 의 editable={false} 는 마운트 이후 반영되므로, 첫 페인트 전에
  // 잠근다(편집 UI 플래시 방지). 렌더 중 외부 객체 변이는 StrictMode/동시 렌더에서 부작용이
  // 될 수 있어 useLayoutEffect(페인트 전 실행)로 옮김 — BlockNote 0.51 은 생성 옵션에
  // editable 이 없어 setter(editor.isEditable) 사용이 공식 경로.
  // syncedBlock 은 비로그인(anon)이라 RLS 로 못 읽으면 블록 내부에서 "로그인 필요" 폴백 표시.
  useLayoutEffect(() => {
    if (editor && editor.isEditable) editor.isEditable = false;
  }, [editor]);

  if (!ystate || !blocks) {
    return <div className="pp-empty">빈 문서</div>;
  }

  return <BlockNoteView editor={editor} editable={false} theme={theme} />;
}
